import React, { useState, useEffect } from 'react';
import './AppHome.css';
import '../Admin/Admin.css';
import Header from "../Home/Header";
import { useNavigate } from 'react-router-dom';
import { MdVisibility, MdEdit, MdDelete } from 'react-icons/md';
import axios from 'axios';

const API_URL = 'https://gym-invoice-back.onrender.com/api/branches';

function BranchManagement() {

  const [branches, setBranches] = useState([]);

  const [form, setForm] = useState({
    branchCode: '',
    branchName: '',
    address: '',
    active: true
  });

  const [editIndex, setEditIndex] = useState(null);
  const [viewForm, setViewForm] = useState(null);

  const navigate = useNavigate();

  const [dialog, setDialog] = useState({
    show: false,
    type: "",
    message: "",
    onConfirm: null,
  });


  // =====================================================
  // CHECK LOGIN
  // =====================================================

  useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    if (!role) {
      navigate("/");
    }
  }, [navigate]);


  // =====================================================
  // LOAD BRANCHES
  // =====================================================

  useEffect(() => {
    fetchBranches();
  }, []);


  // =====================================================
  // FETCH BRANCHES
  // =====================================================

  const fetchBranches = async () => {

    try {

      const response = await axios.get(`${API_URL}/active`);

      setBranches(response.data || []);

    } catch (error) {

      console.error("Error fetching branches:", error);

      setDialog({
        show: true,
        type: "error",
        message: "❌ Failed to load branches.",
        onConfirm: null
      });

    }
  };


  // =====================================================
  // HANDLE INPUT CHANGE
  // =====================================================

  const handleChange = (e) => {

    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };


  // =====================================================
  // HANDLE STATUS CHANGE
  // =====================================================

  const handleStatusChange = (e) => {

    setForm((prev) => ({
      ...prev,
      active: e.target.value === "true"
    }));
  };


  // =====================================================
  // ADD / UPDATE BRANCH
  // =====================================================

  const handleAddOrUpdate = async () => {

    // Validation
    if (!form.branchCode.trim()) {

      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Branch Code is required.",
        onConfirm: null
      });

      return;
    }


    if (!form.branchName.trim()) {

      setDialog({
        show: true,
        type: "warning",
        message: "⚠️ Branch Name is required.",
        onConfirm: null
      });

      return;
    }


    try {

      // =================================================
      // UPDATE
      // =================================================

      if (editIndex !== null) {

        const branchId =
          branches[editIndex].id ||
          branches[editIndex]._id;


        const response = await axios.put(
          `${API_URL}/${branchId}`,
          form
        );


        const updated = [...branches];

        updated[editIndex] = response.data;

        setBranches(updated);

        setEditIndex(null);

        setForm({
          branchCode: '',
          branchName: '',
          address: '',
          active: true
        });


        setDialog({
          show: true,
          type: "success",
          message: "✅ Branch updated successfully!",
          onConfirm: null
        });

      }

      // =================================================
      // ADD
      // =================================================

      else {

        const response = await axios.post(
          API_URL,
          form
        );


        setBranches([
          ...branches,
          response.data
        ]);


        setForm({
          branchCode: '',
          branchName: '',
          address: '',
          active: true
        });


        setDialog({
          show: true,
          type: "success",
          message: "✅ Branch added successfully!",
          onConfirm: null
        });

      }

    } catch (error) {

      console.error(
        editIndex !== null
          ? "Update branch error:"
          : "Add branch error:",
        error
      );


      const errorMessage =
        error.response?.data ||
        (
          editIndex !== null
            ? "❌ Failed to update branch."
            : "❌ Failed to add branch."
        );


      setDialog({
        show: true,
        type: "error",
        message: errorMessage,
        onConfirm: null
      });

    }
  };


  // =====================================================
  // VIEW BRANCH
  // =====================================================

  const handleView = (branch) => {

    setViewForm(branch);

    setDialog({
      show: true,
      type: "success",
      message:
        `Viewing Branch:\n` +
        `Code: ${branch.branchCode}\n` +
        `Name: ${branch.branchName}\n` +
        `Address: ${branch.address || "-"}\n` +
        `Status: ${branch.active ? "Active" : "Inactive"}`,
      onConfirm: null
    });
  };


  // =====================================================
  // EDIT BRANCH
  // =====================================================

  const handleEdit = (branch) => {

    const index = branches.findIndex(
      (b) =>
        (b.id || b._id) ===
        (branch.id || branch._id)
    );


    setForm({
      branchCode: branch.branchCode || '',
      branchName: branch.branchName || '',
      address: branch.address || '',
      active: branch.active ?? true
    });


    setEditIndex(index);
  };


  // =====================================================
  // DELETE BRANCH
  // =====================================================

  const handleDelete = (branch) => {

    const branchId =
      branch.id ||
      branch._id;


    setDialog({
      show: true,
      type: "confirm",
      message:
        `⚠️ Are you sure you want to delete branch "${branch.branchName}"?`,

      onConfirm: async () => {

        try {

          await axios.delete(
            `${API_URL}/${branchId}`
          );


          setBranches(
            branches.filter(
              (b) =>
                (b.id || b._id) !== branchId
            )
          );


          setDialog({
            show: true,
            type: "success",
            message: "✅ Branch deleted successfully!",
            onConfirm: null
          });

        } catch (error) {

          console.error(
            "Delete branch error:",
            error
          );


          setDialog({
            show: true,
            type: "error",
            message: "❌ Failed to delete branch.",
            onConfirm: null
          });

        }
      },
    });
  };


  // =====================================================
  // CANCEL EDIT
  // =====================================================

  const handleCancel = () => {

    setEditIndex(null);

    setForm({
      branchCode: '',
      branchName: '',
      address: '',
      active: true
    });
  };


  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = () => {
    navigate('/');
  };


  // =====================================================
  // VIEW
  // =====================================================

  return (

    <div className="dashboard">

      <Header />

      <div className="payment-container">

        <h2>🏢 BRANCH MANAGEMENT</h2>


        {/* =================================================
            ADD / EDIT BRANCH
        ================================================= */}

        <div className="payment-card">

          <h2>
            {editIndex !== null
              ? "📝 Edit Branch"
              : "📝 Add Branch"}
          </h2>


          {/* BRANCH CODE */}

          <input
            type="text"
            name="branchCode"
            value={form.branchCode}
            onChange={handleChange}
            placeholder="Branch Code"
            readOnly={editIndex !== null}
          />


          {/* BRANCH NAME */}

          <input
            type="text"
            name="branchName"
            value={form.branchName}
            onChange={handleChange}
            placeholder="Branch Name"
          />


          {/* ADDRESS */}

          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Branch Address"
          />


          {/* STATUS */}

          <select
            name="active"
            value={form.active ? "true" : "false"}
            onChange={handleStatusChange}
            className="duration-select"
          >

            <option value="true">
              Active
            </option>

            <option value="false">
              Inactive
            </option>

          </select>


          {/* BUTTONS */}

          <div className="filter-buttons">

            <button
              className="pay-button"
              onClick={handleAddOrUpdate}
            >
              {editIndex !== null
                ? "💾 Update"
                : "➕ Add"}
            </button>


            {editIndex !== null && (

              <button
                type="button"
                className="pay-button"
                onClick={handleCancel}
              >
                ❌ Cancel
              </button>

            )}

          </div>

        </div>


        {/* =================================================
            BRANCH TABLE
        ================================================= */}

        <div className="payment-table">

          <table>

            <thead>

              <tr>

                <th>Branch Code</th>

                <th>Branch Name</th>

                <th>Address</th>

                <th>Status</th>

                <th>Actions</th>

              </tr>

            </thead>


            <tbody>

              {branches.length === 0 ? (

                <tr>

                  <td
                    colSpan="5"
                    style={{
                      textAlign: "center",
                      padding: "15px"
                    }}
                  >
                    No branches found.
                  </td>

                </tr>

              ) : (

                branches.map((branch) => (

                  <tr
                    key={
                      branch.id ||
                      branch._id
                    }
                  >

                    <td>
                      {branch.branchCode}
                    </td>


                    <td>
                      {branch.branchName}
                    </td>


                    <td>
                      {branch.address || "-"}
                    </td>


                    <td>
                      {branch.active
                        ? "Active"
                        : "Inactive"}
                    </td>


                    <td>

                      <div className="table-action-buttons">

                        {/* VIEW */}

                        <button
                          onClick={() =>
                            handleView(branch)
                          }
                          className="action-btn view-btn"
                        >
                          <MdVisibility size={20} />
                          View
                        </button>


                        {/* EDIT */}

                        <button
                          onClick={() =>
                            handleEdit(branch)
                          }
                          className="action-btn edit-btn"
                        >
                          <MdEdit size={20} />
                          Edit
                        </button>


                        {/* DELETE */}

                        <button
                          onClick={() =>
                            handleDelete(branch)
                          }
                          className="action-btn delete-btn"
                        >
                          <MdDelete size={20} />
                          Delete
                        </button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>


        {/* =================================================
            DIALOG
        ================================================= */}

        {dialog.show && (

          <div className="dialog-overlay">

            <div
              className={`dialog-box ${dialog.type}`}
            >

              <p>
                {dialog.message}
              </p>


              <div className="dialog-buttons">

                {dialog.type === "confirm" ? (

                  <>

                    <button
                      onClick={() => {

                        dialog.onConfirm &&
                          dialog.onConfirm();

                        setDialog({
                          ...dialog,
                          show: false
                        });

                      }}
                    >
                      Yes
                    </button>


                    <button
                      onClick={() =>
                        setDialog({
                          ...dialog,
                          show: false
                        })
                      }
                    >
                      No
                    </button>

                  </>

                ) : (

                  <button
                    onClick={() =>
                      setDialog({
                        ...dialog,
                        show: false
                      })
                    }
                  >
                    OK
                  </button>

                )}

              </div>

            </div>

          </div>

        )}

      </div>

    </div>
  );
}

export default BranchManagement;